import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Lock, ShieldCheck, CheckCircle, ArrowLeft, Loader2, Building, MapPin, Mail, User } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import confetti from 'canvas-confetti';
export function Checkout() {
  const {
    cart,
    cartTotal,
    clearCart
  } = useAppContext();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal'>('stripe');
  // Redirect if cart is empty
  useEffect(() => {
    if (cart.length === 0 && !isSuccess) {
      navigate('/cart');
    }
  }, [cart.length, isSuccess, navigate]);
  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      setIsSuccess(true);
      clearCart();
      // Trigger confetti
      const duration = 3 * 1000;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: {
            x: 0
          },
          colors: ['#ea580c', '#f97316', '#fbbf24']
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: {
            x: 1
          },
          colors: ['#ea580c', '#f97316', '#fbbf24']
        });
        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }, 2000);
  };
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-slate-950 flex flex-col" data-id="element-1609">
        <Navbar data-id="element-1610" />
        <main className="flex-1 flex items-center justify-center p-4 pt-24" data-id="element-1611">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center relative overflow-hidden" data-id="element-1612">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-fox-500 to-amber-500" data-id="element-1613" />

            <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1614">
              <CheckCircle className="text-emerald-500 w-10 h-10" data-id="element-1615" />
            </div>

            <h1 className="text-3xl font-heading font-bold text-white mb-4" data-id="element-1616">
              Payment Successful!
            </h1>

            <p className="text-slate-400 mb-8" data-id="element-1617">
              Thank you for your purchase. Your order has been processed and
              your services are being provisioned. We've sent a receipt to your
              email.
            </p>

            <div className="bg-slate-950 rounded-xl p-4 mb-8 border border-slate-800" data-id="element-1618">
              <div className="flex justify-between text-sm mb-2" data-id="element-1619">
                <span className="text-slate-500" data-id="element-1620">Order Number</span>
                <span className="text-white font-mono" data-id="element-1621">
                  #FOX-{Math.floor(100000 + Math.random() * 900000)}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1622">
                <span className="text-slate-500" data-id="element-1623">Amount Paid</span>
                <span className="text-white font-bold" data-id="element-1624">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>
            </div>

            <Link to="/dashboard/customer" className="block w-full bg-fox-600 hover:bg-fox-500 text-white font-medium py-3 rounded-xl transition-all shadow-[0_0_15px_rgba(234,88,12,0.2)]" data-id="element-1625">
              Go to Dashboard
            </Link>
          </motion.div>
        </main>
        <Footer data-id="element-1626" />
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col" data-id="element-1627">
      <Navbar data-id="element-1628" />
      <main className="flex-1 pt-32 pb-24" data-id="element-1629">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl" data-id="element-1630">
          <div className="mb-8" data-id="element-1631">
            <Link to="/cart" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium" data-id="element-1632">
              <ArrowLeft size={16} data-id="element-1633" /> Back to Cart
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-1634">
            {/* Left Column: Checkout Form */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-8" data-id="element-1635">
              <div data-id="element-1636">
                <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1637">
                  Checkout
                </h1>
                <p className="text-slate-400" data-id="element-1638">
                  Complete your payment to activate your services.
                </p>
              </div>

              <form id="checkout-form" onSubmit={handlePayment} className="space-y-8" data-id="element-1639">
                {/* Billing Details */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8" data-id="element-1640">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1641">
                    <User className="text-fox-500" size={20} data-id="element-1642" />
                    Billing Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1643">
                    <div className="space-y-2 md:col-span-2" data-id="element-1644">
                      <label className="text-sm font-medium text-slate-300" data-id="element-1645">
                        Full Name
                      </label>
                      <div className="relative" data-id="element-1646">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1647" />
                        <input required type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="John Doe" data-id="element-1648" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-1649">
                      <label className="text-sm font-medium text-slate-300" data-id="element-1650">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-1651">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1652" />
                        <input required type="email" className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="john@example.com" data-id="element-1653" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-1654">
                      <label className="text-sm font-medium text-slate-300" data-id="element-1655">
                        Company (Optional)
                      </label>
                      <div className="relative" data-id="element-1656">
                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1657" />
                        <input type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="Acme Inc." data-id="element-1658" />
                      </div>
                    </div>

                    <div className="space-y-2 md:col-span-2" data-id="element-1659">
                      <label className="text-sm font-medium text-slate-300" data-id="element-1660">
                        Street Address
                      </label>
                      <div className="relative" data-id="element-1661">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1662" />
                        <input required type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="123 Main St" data-id="element-1663" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-1664">
                      <label className="text-sm font-medium text-slate-300" data-id="element-1665">
                        City
                      </label>
                      <input required type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="New York" data-id="element-1666" />
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1667">
                      <div className="space-y-2" data-id="element-1668">
                        <label className="text-sm font-medium text-slate-300" data-id="element-1669">
                          State/Prov
                        </label>
                        <input required type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="NY" data-id="element-1670" />
                      </div>
                      <div className="space-y-2" data-id="element-1671">
                        <label className="text-sm font-medium text-slate-300" data-id="element-1672">
                          ZIP/Postal
                        </label>
                        <input required type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:border-fox-500 focus:outline-none" placeholder="10001" data-id="element-1673" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8" data-id="element-1674">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1675">
                    <CreditCard className="text-fox-500" size={20} data-id="element-1676" />
                    Payment Method
                  </h2>

                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1677">
                    <button type="button" onClick={() => setPaymentMethod('stripe')} className={`p-4 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${paymentMethod === 'stripe' ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-1678">
                      <CreditCard size={24} className={paymentMethod === 'stripe' ? 'text-fox-500' : 'text-slate-400'} data-id="element-1679" />
                      <span className={`font-medium ${paymentMethod === 'stripe' ? 'text-white' : 'text-slate-400'}`} data-id="element-1680">
                        Credit Card
                      </span>
                    </button>

                    <button type="button" onClick={() => setPaymentMethod('paypal')} className={`p-4 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-1681">
                      <svg className={`w-6 h-6 ${paymentMethod === 'paypal' ? 'text-blue-500' : 'text-slate-400'}`} viewBox="0 0 24 24" fill="currentColor" data-id="element-1682">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" data-id="element-1683" />
                      </svg>
                      <span className={`font-medium ${paymentMethod === 'paypal' ? 'text-white' : 'text-slate-400'}`} data-id="element-1684">
                        PayPal
                      </span>
                    </button>
                  </div>

                  <AnimatePresence mode="wait" data-id="element-1685">
                    {paymentMethod === 'stripe' ? <motion.div key="stripe" initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="space-y-4" data-id="element-1686">
                        <div className="space-y-2" data-id="element-1687">
                          <label className="text-sm font-medium text-slate-300" data-id="element-1688">
                            Card Information
                          </label>
                          <div className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 flex items-center justify-between" data-id="element-1689">
                            <div className="flex items-center gap-3 text-slate-500" data-id="element-1690">
                              <CreditCard size={20} data-id="element-1691" />
                              <span className="text-sm" data-id="element-1692">
                                Card number, expiration, and CVC
                              </span>
                            </div>
                            <div className="flex gap-1" data-id="element-1693">
                              <div className="w-8 h-5 bg-slate-800 rounded" data-id="element-1694" />
                              <div className="w-8 h-5 bg-slate-800 rounded" data-id="element-1695" />
                              <div className="w-8 h-5 bg-slate-800 rounded" data-id="element-1696" />
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-2" data-id="element-1697">
                            This is a secure, encrypted connection. Your card
                            details are never stored on our servers.
                          </p>
                        </div>
                      </motion.div> : <motion.div key="paypal" initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="text-center py-6 bg-slate-950 rounded-xl border border-slate-800" data-id="element-1698">
                        <p className="text-slate-400 mb-4" data-id="element-1699">
                          You will be redirected to PayPal to complete your
                          purchase securely.
                        </p>
                        <div className="inline-block px-8 py-3 bg-[#ffc439] rounded-full font-bold text-[#003087] cursor-not-allowed opacity-80" data-id="element-1700">
                          Pay with PayPal
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </form>
            </div>

            {/* Right Column: Order Summary */}
            <div className="lg:col-span-5 xl:col-span-4" data-id="element-1701">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sticky top-28" data-id="element-1702">
                <h2 className="text-xl font-heading font-bold text-white mb-6" data-id="element-1703">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar" data-id="element-1704">
                  {cart.map(item => <div key={`${item.packageId}-${item.billing}`} className="flex gap-4" data-id="element-1705">
                      <div className="w-12 h-12 bg-slate-950 rounded-lg flex items-center justify-center shrink-0 border border-slate-800" data-id="element-1706">
                        <span className="text-fox-500 font-bold text-sm" data-id="element-1707">
                          x{item.quantity}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1708">
                        <h4 className="text-white font-medium text-sm truncate" data-id="element-1709">
                          {item.name}
                        </h4>
                        <p className="text-slate-500 text-xs" data-id="element-1710">{item.billing}</p>
                      </div>
                      <div className="text-right shrink-0" data-id="element-1711">
                        <span className="text-white font-medium text-sm" data-id="element-1712">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>)}
                </div>

                <div className="border-t border-slate-800 pt-4 mb-6 space-y-3" data-id="element-1713">
                  <div className="flex justify-between text-sm" data-id="element-1714">
                    <span className="text-slate-400" data-id="element-1715">Subtotal</span>
                    <span className="text-white font-medium" data-id="element-1716">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1717">
                    <span className="text-slate-400" data-id="element-1718">Taxes</span>
                    <span className="text-white font-medium" data-id="element-1719">$0.00</span>
                  </div>
                </div>

                <div className="border-t border-slate-800 pt-4 mb-8" data-id="element-1720">
                  <div className="flex justify-between items-center" data-id="element-1721">
                    <span className="text-white font-bold text-lg" data-id="element-1722">
                      Total Due
                    </span>
                    <span className="text-fox-500 font-bold text-2xl" data-id="element-1723">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button type="submit" form="checkout-form" disabled={isProcessing} className="w-full bg-fox-600 hover:bg-fox-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(234,88,12,0.2)] hover:shadow-[0_0_25px_rgba(234,88,12,0.4)] disabled:shadow-none" data-id="element-1724">
                  {isProcessing ? <>
                      <Loader2 size={20} className="animate-spin" data-id="element-1725" />
                      Processing Payment...
                    </> : <>
                      <Lock size={18} data-id="element-1726" />
                      Pay ${cartTotal.toFixed(2)}
                    </>}
                </button>

                <div className="mt-6 flex items-center justify-center gap-4 text-slate-500 text-xs" data-id="element-1727">
                  <div className="flex items-center gap-1" data-id="element-1728">
                    <ShieldCheck size={14} data-id="element-1729" />
                    <span data-id="element-1730">256-bit SSL</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-1731">
                    <Lock size={14} data-id="element-1732" />
                    <span data-id="element-1733">Secure Checkout</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1734" />
    </motion.div>;
}